import { IMsalContext } from "@azure/msal-react";
import { getAccessToken } from "config/token";
import { getHeaders } from "helpers";
import { $nexus_api_base_url_v1 } from "../../http";
import { useCallback, useMemo, useRef, useState } from "react";
import { isDevelopment } from "const";

type IProps = {
  msalInstance: IMsalContext;
  api: string;
  limit?: number;
};

type ResponseData = {
  id: string;
  name: string;
  mail?: string;
};

const REQUEST_COUNT = 10;
const TIME_DELAY = 500;

export const useSearchAD = ({ msalInstance, api, limit = 20 }: IProps) => {
  const [results, setResults] = useState<ResponseData[]>([]);
  const [loadingSet, setLoadingSet] = useState<Set<string>>(new Set());

  const loadersSet = useRef<Set<string>>(new Set());

  const handleSearch = useCallback(
    async (
      value: string,
      {
        id = "",
        requestCount = 0,
      }: {
        id?: string;
        requestCount?: number;
      },
    ) => {
      const search = value.replace(/"/g, "").trim();

      if (!search) {
        setResults([]);
        return;
      }

      loadersSet.current.add(id);

      setLoadingSet(new Set(loadersSet.current));

      const token = await getAccessToken(msalInstance);

      if (!token && !isDevelopment) return;

      $nexus_api_base_url_v1
        .get(`${api}?q=${encodeURIComponent(search)}&limit=${limit}`, {
          headers: getHeaders(token?.token),
        })
        .then(({ data }) => setResults(Array.isArray(data?.data) ? data.data : []))
        .catch(error => {
          if (requestCount >= REQUEST_COUNT) return;

          const param = new URL(error.request.responseURL).searchParams.get(
            "q",
          );

          setTimeout(
            () =>
              handleSearch(param || "", {
                id,
                requestCount: requestCount + 1,
              }),
            TIME_DELAY,
          );
        })
        .finally(() => {
          loadersSet.current.delete(id);
          setLoadingSet(new Set(loadersSet.current));
        });
    },
    [msalInstance, api, limit],
  );

  return useMemo(
    () => ({ results, loadingSet, handleSearch }),
    [results, loadingSet, handleSearch],
  );
};
